import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const res = await login(email, password);
      if (res && res.needsTenant) {
        // Modo multi-tenant: sempre passa pela seleção explícita, mesmo com 1 empresa.
        navigate("/selecionar-empresa");
        return;
      }
      toast.success("Login realizado.");
      navigate("/dashboard");
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <div className="absolute inset-0 -z-0 bg-[url('https://images.pexels.com/photos/21881554/pexels-photo-21881554.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')] bg-cover bg-center opacity-10" />
      <div className="relative w-full max-w-md bg-white border border-slate-200 rounded-2xl shadow-sm p-8">
        <div className="flex items-center gap-2 mb-6">
          <div className="w-9 h-9 rounded-md bg-slate-900 grid place-items-center text-white font-heading font-bold">R</div>
          <span className="font-heading text-lg font-semibold tracking-tight">Ruma Flow</span>
        </div>
        <h1 className="font-heading text-2xl font-semibold tracking-tight text-slate-900">Bem-vindo de volta</h1>
        <p className="text-sm text-slate-500 mt-1">Acesse sua conta para continuar.</p>

        <form onSubmit={onSubmit} className="mt-6 space-y-4" data-testid="login-form">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">E-mail</label>
            <input
              type="email"
              required
              autoComplete="email"
              data-testid="login-email-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full h-10 rounded-lg border border-slate-200 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent"
              placeholder="seu@email.com"
            />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="block text-sm font-medium text-slate-700">Senha</label>
              <Link to="/esqueci-senha" className="text-xs text-slate-600 hover:text-slate-900" data-testid="forgot-link">Esqueci a senha</Link>
            </div>
            <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              required
              autoComplete="current-password"
              data-testid="login-password-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full h-10 rounded-lg border border-slate-200 pl-3 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent"
              placeholder="Sua senha"
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
              className="absolute inset-y-0 right-0 flex items-center px-2 text-slate-400 hover:text-slate-700"
              data-testid="login-password-toggle"
            >
              {showPassword ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
            </button>
            </div>
          </div>
          {error && (
            <div role="alert" data-testid="login-error" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>
          )}
          <button
            type="submit"
            disabled={submitting}
            data-testid="login-submit-button"
            className="w-full h-10 rounded-lg bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 disabled:opacity-50"
          >
            {submitting ? "Entrando..." : "Entrar"}
          </button>
        </form>

        <p className="text-sm text-slate-500 mt-6 text-center">
          Não tem conta?{" "}
          <Link to="/cadastro" className="text-slate-900 font-medium hover:underline" data-testid="signup-link">Criar conta</Link>
        </p>
      </div>
    </div>
  );
}
